/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ColorPickerWithAlpha.tsx
 🗓️ Created: 2025-12-13
 🧩 Description:
    Color picker component with alpha/transparency support.
    Handles both 6-digit (#RRGGBB) and 8-digit (#RRGGBBAA) hex colors.

 🔧 Notes:
    - Native color input only supports RGB, so we handle alpha separately
    - Includes visual slider for opacity adjustment
    - Shows color preview with transparency (checkerboard pattern)
    - Supports both formats: #RRGGBB and #RRGGBBAA
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect, useCallback } from 'react';
import { Input } from './input';

interface ColorPickerWithAlphaProps {
    value: string;
    onChange: (color: string) => void;
    placeholder?: string;
    className?: string;
    showAlphaSlider?: boolean;
}

// Helper functions to convert between formats
const hexToRgba = (hex: string): { r: number; g: number; b: number; a: number } => {
    // Remove # if present
    hex = hex.replace(/^#/, '');

    let r = 0, g = 0, b = 0, a = 1;

    if (hex.length === 6) {
        // #RRGGBB
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
        a = 1;
    } else if (hex.length === 8) {
        // #RRGGBBAA
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
        a = parseInt(hex.substring(6, 8), 16) / 255;
    } else if (hex.length === 3) {
        // #RGB
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
        a = 1;
    } else if (hex.length === 4) {
        // #RGBA
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
        a = parseInt(hex[3] + hex[3], 16) / 255;
    }

    return { r, g, b, a };
};

const rgbaToHex = (r: number, g: number, b: number, a: number): string => {
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    const alphaHex = Math.round(a * 255).toString(16).padStart(2, '0');

    if (a === 1) {
        return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
    }
    return `#${toHex(r)}${toHex(g)}${toHex(b)}${alphaHex}`;
};

const getBaseColor = (hex: string): string => {
    hex = hex.replace(/^#/, '');
    if (hex.length === 8) {
        return `#${hex.substring(0, 6)}`;
    }
    if (hex.length === 4) {
        return `#${hex.substring(0, 3)}`;
    }
    return `#${hex}`;
};

export const ColorPickerWithAlpha: React.FC<ColorPickerWithAlphaProps> = ({
    value,
    onChange,
    placeholder = '#ffffff',
    className = '',
    showAlphaSlider = true,
}) => {
    const [internalValue, setInternalValue] = useState(value || '');
    const [opacity, setOpacity] = useState(100);

    // Parse the initial value to extract opacity
    useEffect(() => {
        if (value) {
            const rgba = hexToRgba(value);
            setOpacity(Math.round(rgba.a * 100));
            setInternalValue(value);
        }
    }, [value]);

    // Get the base color (without alpha) for the native color input
    const baseColor = value ? getBaseColor(value) : '#ffffff';

    // Handle color change from native picker
    const handleColorChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newColor = e.target.value;
        const rgba = hexToRgba(newColor);
        const newHex = rgbaToHex(rgba.r, rgba.g, rgba.b, opacity / 100);
        setInternalValue(newHex);
        onChange(newHex);
    }, [opacity, onChange]);

    // Handle opacity slider change
    const handleOpacityChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newOpacity = parseInt(e.target.value);
        setOpacity(newOpacity);

        if (internalValue) {
            const rgba = hexToRgba(internalValue);
            const newHex = rgbaToHex(rgba.r, rgba.g, rgba.b, newOpacity / 100);
            setInternalValue(newHex);
            onChange(newHex);
        }
    }, [internalValue, onChange]);

    // Handle text input change
    const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInternalValue(newValue);

        // Only call onChange if it's a valid hex color
        if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{4}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(newValue)) {
            const rgba = hexToRgba(newValue);
            setOpacity(Math.round(rgba.a * 100));
            onChange(newValue);
        }
    }, [onChange]);

    // Create the preview style with transparency
    const previewStyle: React.CSSProperties = {
        background: `linear-gradient(45deg, #ccc 25%, transparent 25%), 
                 linear-gradient(-45deg, #ccc 25%, transparent 25%), 
                 linear-gradient(45deg, transparent 75%, #ccc 75%), 
                 linear-gradient(-45deg, transparent 75%, #ccc 75%)`,
        backgroundSize: '8px 8px',
        backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
    };

    const rgba = internalValue ? hexToRgba(internalValue) : { r: 255, g: 255, b: 255, a: 1 };
    const colorOverlayStyle: React.CSSProperties = {
        backgroundColor: `rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`,
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
    };

    return (
        <div className={`flex items-center gap-2 ${className}`}>
            {/* Color preview with transparency */}
            <div
                className="relative h-10 w-12 cursor-pointer rounded border border-input overflow-hidden flex-shrink-0"
                style={previewStyle}
                onClick={() => document.getElementById(`color-picker-${value}`)?.click()}
            >
                <div style={colorOverlayStyle} />
                <input
                    id={`color-picker-${value}`}
                    type="color"
                    value={baseColor}
                    onChange={handleColorChange}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                />
            </div>

            {/* Text input */}
            <Input
                value={internalValue}
                onChange={handleTextChange}
                placeholder={placeholder}
                className="flex-1"
            />

            {/* Alpha slider */}
            {showAlphaSlider && (
                <div className="flex items-center gap-2 flex-shrink-0">
                    <div className="relative w-20 h-6">
                        {/* Slider track with gradient */}
                        <div
                            className="absolute inset-0 rounded"
                            style={{
                                ...previewStyle,
                                backgroundSize: '6px 6px',
                            }}
                        >
                            <div
                                className="absolute inset-0 rounded"
                                style={{
                                    background: `linear-gradient(to right, 
                    rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, 0) 0%, 
                    rgba(${rgba.r}, ${rgba.g}, ${rgba.b}, 1) 100%)`,
                                }}
                            />
                        </div>
                        <input
                            type="range"
                            min="0"
                            max="100"
                            value={opacity}
                            onChange={handleOpacityChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        />
                        {/* Slider thumb indicator */}
                        <div
                            className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border-2 border-gray-400 rounded-full shadow pointer-events-none"
                            style={{ left: `calc(${opacity}% - 6px)` }}
                        />
                    </div>
                    <span className="text-xs text-muted-foreground w-8 text-right">{opacity}%</span>
                </div>
            )}
        </div>
    );
};

export default ColorPickerWithAlpha;
